import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

/**
 * useInViewOnce — fires once when element enters viewport.
 * Falls back to true immediately if already in view or on unsupported browsers.
 */
function useInViewOnce(rootMargin = "0px") {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Immediate check for initial-in-viewport
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setInView(true);
      return;
    }
    if (typeof IntersectionObserver === "undefined") { setInView(true); return; }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setInView(true); io.disconnect(); }
      },
      { rootMargin, threshold: 0 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin]);

  return [ref, inView];
}

/**
 * MaskReveal — line/word based masked "slide-up from behind an overflow-hidden window".
 * Wrap each line: <MaskLine delay={0.1}>YOUR LINE</MaskLine>
 */
export const MaskLine = ({ children, delay = 0, duration = 0.9, className = "" }) => (
  <span className={`mask-line ${className}`}>
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

/** MaskLineScroll — fires when the element scrolls into view (or immediately if already there). */
export const MaskLineScroll = ({ children, delay = 0, duration = 0.9, className = "" }) => {
  const [ref, inView] = useInViewOnce();
  return (
    <span ref={ref} className={`mask-line ${className}`}>
      <motion.span
        initial={{ y: "110%" }}
        animate={inView ? { y: 0 } : { y: "110%" }}
        transition={{ duration, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
};

/** FadeUp — soft rise + fade using the same in-view hook */
export const FadeUp = ({ children, delay = 0, y = 24, className = "" }) => {
  const [ref, inView] = useInViewOnce("100px");
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};
